import React from "react";

import { businessHealthScorePalette } from "../../../../Utils/Helper/businessHealth";
import { extractFlattenedFieldAPI } from "../../../../Utils/WatchlistSummary/validation";
import {
  OrganizationRelevanceSection,
  KPIMetricsSection,
  LeadershipTrendsSection,
  RiskAssessmentSection,
  TopLeadershipTeamSection,
  ExecutiveBoardChangesSection,
  OrganizationalChangesSection,
  CulturalInitiativesSection,
} from "../LeadershipSection/helpers";

// 🎯 Types and Interfaces
interface LeadershipData {
  synopsis?: string;
  metadata?: {
    confidence_score?: number;
    last_updated?: string;
    data_freshness?: string;
    sources?: Array<string | { name: string }>;
  };
  top10Execs?: Executive[];
  kpis?: {
    newHires?: number;
    promotions?: number;
    retentionRate?: number;
    employeeGrowth?: number;
    totalEmployees?: number;
    executiveTurnover?: number;
  };
  trends?: {
    "4weekTrend"?: {
      direction?: string;
      keyChanges?: string;
      significance?: string;
    };
    quarterlyComparison?: {
      keyHires?: string;
      executiveChanges?: string;
      organizationalShifts?: string;
    };
  };
  riskAssessment?: {
    culturalRisk?: string;
    keyPersonRisk?: string;
    overallStability?: string;
    successionPlanning?: string;
  };
  executiveChanges?: any[];
  boardChanges?: any[];
  organizationalChanges?: any[];
  culturalInitiatives?: any[];
  organizationRelevance?: {
    sentiment?: string;
    relevance?: string;
    keyConnections?: string;
    strategicImplications?: string;
    competitiveIntelligence?: string;
    partnershipOpportunities?: string;
    riskFactors?: string;
  };
}

interface Executive {
  name: string;
  title: string;
  [key: string]: any;
}

interface ScoreBubbleProps {
  sectionScore: number;
  healthFlag: string;
}

interface LeadershipModalHeaderProps {
  accountName: string;
  sectionData: any;
  onToggleAudio?: () => void;
}

interface LeadershipModalBodyProps {
  synopsis?: string;
  confidenceScore?: number;
  dataFreshness?: string;
  lastUpdated?: string;
  sources?: Array<string | { name: string }>;
  sectionData: any;
  organizationName?: string;
}

// 🎯 Score Bubble Component (Reused from Financial)
const LeadershipScoreBubble: React.FC<ScoreBubbleProps> = ({
  sectionScore,
}) => {
  const getScoreColors = (score: number) => businessHealthScorePalette(score);

  const colors = getScoreColors(sectionScore);

  return (
    <div
      style={{
        position: "relative",
        width: "48px",
        height: "48px",
        flexShrink: 0,
      }}
    >
      {/* Outer circle */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "48px",
          height: "48px",
          borderRadius: "50%",
          background: colors.outer,
          border: `2px solid ${colors.inner}`,
        }}
      />
      {/* Inner circle */}
      <div
        style={{
          position: "absolute",
          top: "6px",
          left: "6px",
          width: "36px",
          height: "36px",
          borderRadius: "50%",
          background: colors.inner,
          border: `1px solid ${colors.score}`,
        }}
      />
      {/* Score circle (superscript) */}
      <div
        style={{
          position: "absolute",
          top: "-2px",
          right: "-2px",
          width: "20px",
          height: "20px",
          borderRadius: "50%",
          background: colors.score,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "2px solid white",
        }}
      >
        <span
          style={{
            color: "white",
            fontSize: "11px",
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          {Math.round(sectionScore)}
        </span>
      </div>
    </div>
  );
};

// 🎯 Modal Header Component
const LeadershipModalHeader: React.FC<LeadershipModalHeaderProps> = ({
  accountName,
  sectionData,
  onToggleAudio,
}) => {
  const sectionScore = sectionData?.sectionScore || 0;
  const healthFlag = sectionData?.sectionHealthFlag || "N/A";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "16px",
        marginBottom: "16px",
      }}
    >
      <LeadershipScoreBubble
        sectionScore={sectionScore}
        healthFlag={healthFlag}
      />
      <div>
        <h3 style={{ margin: 0, fontSize: "18px", color: "#8b5cf6" }}>
          <strong>{accountName}</strong> - <em>Leadership & Org Changes</em>
        </h3>
      </div>
      <button
        id="leadership-audio-btn"
        className="audio-btn"
        title="Play leadership audio narration"
        onClick={
          onToggleAudio ||
          (() => {
            // No-op: audio toggle not implemented
          })
        }
        style={{
          background: "none",
          border: "none",
          padding: "8px",
          borderRadius: "6px",
          cursor: "pointer",
          transition: "background-color 0.2s",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#8b5cf6"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M11 5 6 9H2v6h4l5 4V5ZM15.54 8.46a5 5 0 0 1 0 7.07M19.07 4.93a10 10 0 0 1 0 14.14" />
        </svg>
      </button>
    </div>
  );
};

// 🎯 Badge Components
const HealthBadge: React.FC<{ flag: string; text: string }> = ({
  flag,
  text,
}) => {
  const bgColor =
    flag === "G" ? "#16a34a" : flag === "Y" ? "#d97706" : "#dc2626";

  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "4px 12px",
        background: bgColor,
        borderRadius: "20px",
        fontSize: "12px",
        fontWeight: 500,
      }}
    >
      <span style={{ color: "white" }}>Account Health:</span>
      <span style={{ color: "white", fontWeight: 700 }}>{text}</span>
    </div>
  );
};

const RelevanceBadge: React.FC<{
  flag: string;
  text: string;
  organizationName?: string;
}> = ({ text, organizationName }) => {
  const orgName = organizationName || "Organization";

  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "4px 12px",
        background: "#8b5cf6",
        borderRadius: "20px",
        fontSize: "12px",
        fontWeight: 500,
      }}
    >
      <span style={{ color: "white" }}>Relevance to {orgName}:</span>
      <span style={{ color: "white", fontWeight: 700 }}>{text}</span>
    </div>
  );
};

// 🎯 Modal Body Component
const LeadershipModalBody: React.FC<LeadershipModalBodyProps> = ({
  synopsis,
  confidenceScore,
  dataFreshness,
  sources,
  sectionData,
  organizationName,
}) => {
  const healthFlag = sectionData?.sectionHealthFlag || "N/A";
  const relevanceFlag = sectionData?.sectionRelevanceFlag || "N/A";

  // Process sources with null safety
  const sourcesText = Array.isArray(sources)
    ? sources
        .map(s => (typeof s === "object" ? s?.name : s))
        .filter(Boolean)
        .join(", ")
    : null;

  // Format confidence score with color coding
  const formatConfidenceScore = (
    score: any
  ): { text: string; color: string } => {
    if (score === null || score === undefined)
      return { text: "N/A", color: "#6b7280" };
    if (typeof score === "number") {
      const percentage =
        score <= 1 ? Math.round(score * 100) : Math.round(score);
      let color = "#6b7280";
      if (percentage >= 80) color = "#16a34a";
      else if (percentage >= 50) color = "#d97706";
      else color = "#dc2626";
      return { text: percentage + "%", color };
    }
    return { text: "N/A", color: "#6b7280" };
  };

  // Format freshness with color coding
  const formatFreshness = (freshness: any): { text: string; color: string } => {
    if (!freshness || freshness === "N/A")
      return { text: "N/A", color: "#6b7280" };
    const normalized = String(freshness).toLowerCase();
    let color = "#6b7280";
    if (normalized.includes("recent") || normalized.includes("fresh"))
      color = "#16a34a";
    else if (normalized.includes("moderate") || normalized.includes("medium"))
      color = "#d97706";
    else if (normalized.includes("stale") || normalized.includes("old"))
      color = "#dc2626";
    return { text: freshness, color };
  };

  const confidenceFormatted = formatConfidenceScore(confidenceScore);
  const freshnessFormatted = formatFreshness(dataFreshness);

  const getHealthText = (flag: string) => {
    switch (flag) {
      case "G":
        return "GREEN";
      case "Y":
        return "YELLOW";
      case "R":
        return "RED";
      default:
        return "N/A";
    }
  };

  const getRelevanceText = (flag: string) => {
    switch (flag) {
      case "H":
        return "HIGH";
      case "M":
        return "MEDIUM";
      case "L":
        return "LOW";
      default:
        return "N/A";
    }
  };

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        marginBottom: "16px",
        overflow: "hidden",
      }}
    >
      {/* Health and Relevance Badges */}
      <div
        style={{
          display: "flex",
          gap: "12px",
          padding: "12px 16px",
          background: "#f8fafc",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <HealthBadge flag={healthFlag} text={getHealthText(healthFlag)} />
        <RelevanceBadge
          flag={relevanceFlag}
          text={getRelevanceText(relevanceFlag)}
          organizationName={organizationName}
        />
      </div>

      {/* Synopsis Content */}
      <div style={{ padding: "16px 16px 0px 16px" }} />

      {synopsis && (
        <div style={{ padding: "16px" }}>
          <p
            style={{
              margin: "0 0 12px 0",
              lineHeight: 1.6,
              fontSize: "14px",
              color: "#374151",
            }}
          >
            {synopsis}
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "12px",
              marginTop: "12px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "11px",
                padding: "6px 8px",
                background: "#f8fafc",
                borderRadius: "4px",
              }}
            >
              <span style={{ color: "#64748b", fontWeight: 500 }}>
                Confidence:
              </span>
              <span
                style={{ color: confidenceFormatted.color, fontWeight: 600 }}
              >
                {confidenceFormatted.text}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "11px",
                padding: "6px 8px",
                background: "#f8fafc",
                borderRadius: "4px",
              }}
            >
              <span style={{ color: "#64748b", fontWeight: 500 }}>
                Freshness:
              </span>
              <span
                style={{ color: freshnessFormatted.color, fontWeight: 600 }}
              >
                {freshnessFormatted.text}
              </span>
            </div>
            {sourcesText && (
              <div
                style={{
                  gridColumn: "1 / -1",
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: "11px",
                  padding: "6px 8px",
                  background: "#f8fafc",
                  borderRadius: "4px",
                }}
              >
                <span
                  style={{
                    color: "#64748b",
                    fontWeight: 500,
                    flexShrink: 0,
                    marginRight: "8px",
                  }}
                >
                  Sources:
                </span>
                <span
                  style={{
                    color: "#1e293b",
                    fontWeight: 600,
                    textAlign: "right",
                    wordWrap: "break-word",
                    overflowWrap: "break-word",
                  }}
                >
                  {sourcesText}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// 🎯 Main Leadership Section Component
export const LeadershipSection: React.FC<{
  data: LeadershipData;
  sectionData: any;
  accountName: string;
  organizationName?: string;
  onToggleAudio?: () => void;
}> = ({ data, sectionData, accountName, organizationName, onToggleAudio }) => {
  // Extract core leadership data
  const synopsis = extractFlattenedFieldAPI(data, "synopsis", null);
  const confidenceScore = extractFlattenedFieldAPI(
    data,
    "metadata.confidence_score",
    null
  );
  const lastUpdated = extractFlattenedFieldAPI(
    data,
    "metadata.last_updated",
    null
  );
  const dataFreshness = extractFlattenedFieldAPI(
    data,
    "metadata.data_freshness",
    null
  );
  const sources = extractFlattenedFieldAPI(data, "metadata.sources", []);

  // Extract top leadership team
  const top10Execs = extractFlattenedFieldAPI(data, "top10Execs", []);

  // Extract KPI metrics
  const newHires = extractFlattenedFieldAPI(data, "kpis.newHires", null);
  const promotions = extractFlattenedFieldAPI(data, "kpis.promotions", null);
  const retentionRate = extractFlattenedFieldAPI(
    data,
    "kpis.retentionRate",
    null
  );
  const employeeGrowth = extractFlattenedFieldAPI(
    data,
    "kpis.employeeGrowth",
    null
  );
  const totalEmployees = extractFlattenedFieldAPI(
    data,
    "kpis.totalEmployees",
    null
  );
  const executiveTurnover = extractFlattenedFieldAPI(
    data,
    "kpis.executiveTurnover",
    null
  );

  // Extract trends
  const fourWeekTrendDirection = extractFlattenedFieldAPI(
    data,
    "trends.4weekTrend.direction",
    null
  );
  const fourWeekTrendKeyChanges = extractFlattenedFieldAPI(
    data,
    "trends.4weekTrend.keyChanges",
    null
  );
  const fourWeekTrendSignificance = extractFlattenedFieldAPI(
    data,
    "trends.4weekTrend.significance",
    null
  );
  const quarterlyKeyHires = extractFlattenedFieldAPI(
    data,
    "trends.quarterlyComparison.keyHires",
    null
  );
  const quarterlyExecutiveChanges = extractFlattenedFieldAPI(
    data,
    "trends.quarterlyComparison.executiveChanges",
    null
  );
  const quarterlyOrganizationalShifts = extractFlattenedFieldAPI(
    data,
    "trends.quarterlyComparison.organizationalShifts",
    null
  );

  // Extract risk assessment
  const culturalRisk = extractFlattenedFieldAPI(
    data,
    "riskAssessment.culturalRisk",
    null
  );
  const keyPersonRisk = extractFlattenedFieldAPI(
    data,
    "riskAssessment.keyPersonRisk",
    null
  );
  const overallStability = extractFlattenedFieldAPI(
    data,
    "riskAssessment.overallStability",
    null
  );
  const successionPlanning = extractFlattenedFieldAPI(
    data,
    "riskAssessment.successionPlanning",
    null
  );

  // Extract executive and board changes
  const executiveChanges = extractFlattenedFieldAPI(
    data,
    "executiveChanges",
    []
  );
  const boardChanges = extractFlattenedFieldAPI(data, "boardChanges", []);

  // Extract organizational changes
  const organizationalChanges = extractFlattenedFieldAPI(
    data,
    "organizationalChanges",
    []
  );

  // Extract cultural initiatives
  const culturalInitiatives = extractFlattenedFieldAPI(
    data,
    "culturalInitiatives",
    []
  );

  // Extract organization relevance
  const orgRelevanceSentiment = extractFlattenedFieldAPI(
    data,
    "organizationRelevance.sentiment",
    null
  );
  const orgRelevanceRelevance = extractFlattenedFieldAPI(
    data,
    "organizationRelevance.relevance",
    null
  );
  const keyConnections = extractFlattenedFieldAPI(
    data,
    "organizationRelevance.keyConnections",
    null
  );
  const strategicImplications = extractFlattenedFieldAPI(
    data,
    "organizationRelevance.strategicImplications",
    null
  );
  const competitiveIntelligence = extractFlattenedFieldAPI(
    data,
    "organizationRelevance.competitiveIntelligence",
    null
  );
  const partnershipOpportunities = extractFlattenedFieldAPI(
    data,
    "organizationRelevance.partnershipOpportunities",
    null
  );
  const riskFactors = extractFlattenedFieldAPI(
    data,
    "organizationRelevance.riskFactors",
    null
  );

  return (
    <div className="leadership-section-consolidated">
      <LeadershipModalHeader
        accountName={accountName}
        sectionData={sectionData}
        onToggleAudio={
          onToggleAudio ||
          (() => {
            // No-op: audio toggle not implemented
          })
        }
      />
      <LeadershipModalBody
        synopsis={synopsis}
        confidenceScore={confidenceScore}
        dataFreshness={dataFreshness}
        lastUpdated={lastUpdated}
        sources={sources}
        sectionData={sectionData}
        organizationName={organizationName}
      />
      <OrganizationRelevanceSection
        sentiment={orgRelevanceSentiment}
        relevance={orgRelevanceRelevance}
        keyConnections={keyConnections}
        strategicImplications={strategicImplications}
        competitiveIntelligence={competitiveIntelligence}
        partnershipOpportunities={partnershipOpportunities}
        riskFactors={riskFactors}
        organizationName={organizationName}
      />
      <KPIMetricsSection
        newHires={newHires}
        promotions={promotions}
        retentionRate={retentionRate}
        employeeGrowth={employeeGrowth}
        totalEmployees={totalEmployees}
        executiveTurnover={executiveTurnover}
      />
      <LeadershipTrendsSection
        fourWeekTrendDirection={fourWeekTrendDirection}
        fourWeekTrendKeyChanges={fourWeekTrendKeyChanges}
        fourWeekTrendSignificance={fourWeekTrendSignificance}
        quarterlyKeyHires={quarterlyKeyHires}
        quarterlyExecutiveChanges={quarterlyExecutiveChanges}
        quarterlyOrganizationalShifts={quarterlyOrganizationalShifts}
      />
      <RiskAssessmentSection
        culturalRisk={culturalRisk}
        keyPersonRisk={keyPersonRisk}
        overallStability={overallStability}
        successionPlanning={successionPlanning}
      />
      <TopLeadershipTeamSection top10Execs={top10Execs} />
      <ExecutiveBoardChangesSection
        executiveChanges={executiveChanges}
        boardChanges={boardChanges}
      />
      <OrganizationalChangesSection
        organizationalChanges={organizationalChanges}
      />
      <CulturalInitiativesSection culturalInitiatives={culturalInitiatives} />
    </div>
  );
};
